import * as Yup from "yup";

interface TextValidationOptions {
  title?: string;
  minLength?: number;
  maxLength?: number;
  allowDot?: boolean; // <-- add this option
}

export const createTextValidation = ({
  title = "Text",
  minLength = 1,
  maxLength = 50,
  allowDot = false, // <-- default false
}: TextValidationOptions = {}) => {
  // Build allowed characters regex
  const allowedChars = allowDot
    ? /^[a-zA-Z0-9&\-\s.]+$/
    : /^[a-zA-Z0-9&\-\s]+$/;

  return Yup.string()
    .required(`${title} is required`)
    .min(minLength, `${title} must be at least ${minLength} characters`)
    .max(maxLength, `${title} must be at most ${maxLength} characters`)
    // not only spaces
    .test(
      "no-spaces-only",
      `${title} cannot contain only spaces`,
      (value) => !!value && value.trim().length > 0
    )
    // allowed characters only
    .matches(
      allowedChars,
      allowDot
        ? `${title} can only contain letters, numbers, spaces, '.', '&' and '-'`
        : `${title} can only contain letters, numbers, spaces, '&' and '-'`
    )
    // not only special characters
    .test(
      "not-only-special",
      `${title} cannot be only special characters`,
      (value) => {
        if (!value) return false;
        const onlySpecial = allowDot
          ? /^[&\-\s.]+$/
          : /^[&\-\s]+$/;
        return !onlySpecial.test(value);
      }
    )
    // no consecutive special characters (--, &&, -&, &-)
    .test(
      "no-consecutive-specials",
      `${title} cannot have consecutive special characters (& or -)`,
      (value) => !value || !(/(&{2,}|-{2,}|&-|-&)/.test(value))
    )
    // no multiple spaces
    .test(
      "no-multiple-spaces",
      `${title} cannot have multiple consecutive spaces`,
      (value) => !value || !/\s{2,}/.test(value)
    )
    // no leading/trailing spaces
    .test(
      "no-leading-trailing-spaces",
      `${title} cannot start or end with spaces`,
      (value) => !value || value.trim() === value
    )
    // must start with a letter or number
    .test(
      "first-char-alphanumeric",
      `${title} must start with a letter or number`,
      (value) => !value || /^[a-zA-Z0-9]/.test(value)
    )
    // prevent inputs like "A B C" (all single letters)
    .test(
      "no-all-single-letters",
      `${title} cannot be only single characters separated by spaces`,
      (value) => {
        if (!value) return true;
        const words = value.trim().split(/\s+/);
        return !(words.length > 1 && words.every((w) => w.length === 1));
      }
    );
};
